"use client";

import { Link, usePathname, useTranslations } from "@i18n/next";
import { HallowABFlag, useFlag } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { URL_ONBOARDING, URL_SELF } from "@packages/sdk";
import { Button, useDynamicViewport } from "@packages/ui";
import { useEffect, useMemo, useState } from "react";

import { LINKS } from "../../constants";
import type { MenuBarProps } from "./MenuBar";

type TryHallowForFreeProps = {
  abTests: MenuBarProps["abTests"];
};

export const TryHallowForFree = ({ abTests }: TryHallowForFreeProps) => {
  // note the different min-width, copied from MenuBar.tsx
  const isDesktop = useDynamicViewport({ minimumWidth: 1024 });
  const analytics = useAnalytics();
  const pathname = usePathname();
  const t = useTranslations();
  const { isTargeted: homepageCtaTest } = useFlag({
    key: HallowABFlag.WebHomepageCTADestination,
  });
  const [isClient, setIsClient] = useState<boolean>(false);

  const fallbackUrl = useMemo(() => {
    if (isDesktop) {
      return `${URL_ONBOARDING}/?utm_source=website&utm_medium=website&utm_campaign=Website-NavLink&subscribeRedirect=${encodeURIComponent(`${URL_SELF}${pathname}`)}`;
    }

    return LINKS.tryHallowForFree.props.href;
  }, [isDesktop]);

  const url = useMemo(() => {
    if (pathname !== "/" || !abTests?.[HallowABFlag.WebHomepageCTADestination])
      return fallbackUrl;

    if (homepageCtaTest)
      return abTests[HallowABFlag.WebHomepageCTADestination].treatment;

    return abTests[HallowABFlag.WebHomepageCTADestination].control;
  }, [pathname, fallbackUrl, homepageCtaTest]);

  useEffect(() => {
    setIsClient(typeof window !== "undefined");
  }, []);

  if (!isClient)
    return <Button disabled>{t(LINKS.tryHallowForFree.message)}</Button>;

  return (
    <Button
      asChild
      onClickCapture={() =>
        analytics.track({
          event: HallowAnalyticsEvent.TappedTryHallowForFree,
          properties: {
            button_location: "menu_bar",
          },
        })
      }
      size={isDesktop ? "m" : "l"}
      isFullWidth={!isDesktop}
    >
      <Link {...LINKS.tryHallowForFree.props} href={url}>
        {t(LINKS.tryHallowForFree.message)}
      </Link>
    </Button>
  );
};
